// useDynamicContentLoader.js
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchMap,
  selectiDB,
  selectMap,
  selectVersion,
} from "../../features/counter/counterSlice";
import generateMenuFromXml from "../../components/GuidelineNav/menuGeneration";

export const useDynamicContentLoader = ({ contentPattern, chapterRoot }) => {
  const [htmlContent, setHtmlContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const iDB = useSelector(selectiDB);
  const dartMap = useSelector(selectMap);
  const dispatch = useDispatch();
  const ccmsversion = useSelector(selectVersion);

  const cleanHtml = useCallback((raw) => {
    if (!raw) {
      console.log("Raw HTML is empty or undefined");
      return "";
    }

    try {
      const dom = new DOMParser().parseFromString(raw, "text/html");

      ["ditatitle", "author"].forEach((tag) => {
        dom.querySelectorAll(tag).forEach((el) => el.remove());
      });

      dom.querySelectorAll("xref").forEach((xref) => {
        const href = xref.getAttribute("href");
        if (href) {
          const a = document.createElement("a");
          a.href = href;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          a.innerHTML = xref.innerHTML || href;
          xref.replaceWith(a);
        }
      });

      return dom.body ? dom.body.innerHTML : raw;
    } catch (err) {
      console.error("Error cleaning HTML:", err);
      return raw;
    }
  }, []);

  const loadContent = useCallback(
    async (contentLink) => {
      if (!contentLink) {
        console.error("Invalid content link");
        throw new Error("Invalid content link");
      }

      try {
        console.log("Attempting to load from IndexedDB:", contentLink);
        const iDBValue = await iDB.read(contentLink);
        if (iDBValue) {
          return iDBValue;
        }

        console.log("Not found in IndexedDB, fetching from server...");
        const action = await dispatch(
          fetchMap({
            path: contentLink,
            dartSelectors: { iDB, dispatch, ccmsversion },
          }),
        );

        const data = action.payload?.data;
        if (!data) {
          console.error("No data in response payload");
          throw new Error("No content data received");
        }

        console.log("Successfully fetched from server");
        return data;
      } catch (err) {
        console.error("Failed to load content:", err);
        throw err;
      }
    },
    [iDB, dispatch, ccmsversion],
  );

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!dartMap || !iDB || (chapterRoot && !chapterRoot)) {
          throw new Error("Required data not loaded");
        }

        console.log("Generating menu from XML...");
        const parsed = generateMenuFromXml(`${dartMap}`);
        console.log("Parsed menu:", parsed);

        if (!parsed?.relatedLinks) {
          throw new Error("Document has no related links section");
        }

        const contentLink = parsed.relatedLinks
          ?.find((link) => link.href?.toLowerCase()?.includes(contentPattern))
          ?.href?.replace(/^(\.\.\/)+/, "");

        if (!contentLink) {
          console.warn("Content link not found in:", parsed.relatedLinks);
          throw new Error(
            `Content link (${contentPattern}) not found in the document`,
          );
        }

        console.log("Found content link:", contentLink);
        const raw = await loadContent(contentLink);

        if (isMounted) {
          const cleaned = cleanHtml(raw);
          // console.log("Cleaned HTML content:", cleaned);
          setHtmlContent(cleaned || "No content available");
          setError(null);
        }
      } catch (err) {
        console.error("Content loading error:", err);
        if (isMounted) {
          setError(err.message);
          setHtmlContent("");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [iDB, chapterRoot, loadContent, cleanHtml, contentPattern]);

  return { htmlContent, isLoading, error };
};
